import React from 'react';
import { generateRandomCode } from 'src/@vymo/ui/molecules/form/vymoComponentsWithForm/aif/queries';
import { ReactComponent as CallIcon } from 'src/assets/icons/call.svg';
import { ReactComponent as MessageIcon } from 'src/assets/icons/email.svg';
import { ReactComponent as EmailAt } from 'src/assets/icons/emailAt.svg';
import { ReactComponent as LocationIcon } from 'src/assets/icons/geoPointer.svg';
import { Keys, locale } from 'src/i18n';
import { maskValue } from 'src/models/lmsConfig/queries';
import { REVYRE } from '../constants';
import {
  DomainType,
  EntityEmail,
  EntityLocation,
  EntityPhoneNumber,
  EntityProfile,
  WorkflowType,
} from '../types';

export const getActivityCardName = (cardCode) => {
  switch (cardCode) {
    case 'completed':
      return 'Your Last Engagements';
    case 'pending':
      return locale(Keys.PENDING_ACTIVITIES);
    case 'scheduled':
      return locale(Keys.SCHEDULED_ACTIVITIES);
    default:
      return '';
  }
};

export const getQuickActionButtonHint = (type) => {
  switch (type) {
    case 'email':
      return locale(Keys.EMAIL);
    case 'navigate':
      return locale(Keys.NAVIGATE);
    case 'print':
      return locale(Keys.PRINT);
    case 'call':
      return 'Call';
    case 'sms':
      return 'SMS';
    default:
      return '';
  }
};

export const getActionButtons = (workflowType) => [
  {
    hint: 'Update',
    scenario: 'workflow', // MYTODO :: Need to check how to identify if its workflow update or activity update
    type: 'update',
    workflowType,
    disabled: false, // MYTODO :: Need to disable based on some condition
  },
];

export const getReferredActions = (
  domainType: DomainType,
  referredDoDetails,
  workflowType?: WorkflowType,
) => {
  const referredActions: any = [];

  // TODO - right now hardcoded need to come through RBAC
  if (domainType === 'workflow') {
    referredActions.push({
      code: 'activity',
      hint: locale(Keys.ADD_ACTIVITY),
      scenario: 'activity',
      type: 'detailsCreate',
      domainType: 'activity',
      disabled: false,
    });
  }

  referredActions.push(
    ...(referredDoDetails || []).map((linkedWorkflow) => {
      if (
        REVYRE.COLLECT_IQ.DETAILS_PAGE_CUSTOM_REFFERED_ENTITIES.includes(
          linkedWorkflow.workflowType,
        )
      ) {
        return {
          hint: `Collect ${linkedWorkflow?.flowName}`,
          scenario: 'workflow',
          type: 'update',
          domainType: linkedWorkflow.workflowType,
          workflowType,
          disabled: false,
          buttonMetadata: {
            customUpdateState: 'collectPayment',
          },
        };
      }
      return {
        hint: `Add ${linkedWorkflow?.flowName}`,
        scenario: 'workflow',
        type: 'detailsCreate',
        domainType: 'workflow',
        workflowType: linkedWorkflow?.workflowType,
        disabled: false,
      };
    }),
  );

  return referredActions;
};

export const transformQuickAction = (
  action: { type: string; items: any[] },
  profiles: EntityProfile[],
) => {
  const fieldMap: Record<string, keyof EntityProfile> = {
    call: 'phoneNumbers',
    sms: 'phoneNumbers',
    email: 'emailIDs',
    navigate: 'locations',
  };

  const fieldToAdd = fieldMap[action.type];
  if (!fieldToAdd) return action;

  const transformedItems = profiles.flatMap((profile) => {
    const items = profile?.[fieldToAdd] as
      | EntityPhoneNumber[]
      | EntityEmail[]
      | EntityLocation[];
    if (!items || items.length === 0) {
      return [];
    }

    const processedItems = items.map((item) => {
      if (action.type === 'call') {
        return {
          ...item,
          profileId: profile?.profileId,
        };
      }

      if (!item?.masked) return item;

      if (action.type === 'navigate') {
        return {
          ...item,
          value: {
            ...item.value,
            address_string: maskValue(item?.value?.address_string, 1),
          },
        };
      }

      return {
        ...item,
        value: maskValue(item.value, 1),
      };
    });

    return [
      {
        type: profile.type,
        name: profile.name,
        description: profile.description,
        items: processedItems,
      },
    ];
  });

  return {
    ...action,
    items: transformedItems,
  };
};

export const buildProfileActionOptions = (action) => {
  const { type: actionType, items } = action;

  const configMap = {
    call: {
      icon: <CallIcon />,
      getSubLabel: (item) => item.value,
    },
    sms: {
      icon: <MessageIcon />,
      getSubLabel: (item) => item.value,
    },
    email: {
      icon: <EmailAt />,
      getSubLabel: (item) => item.value,
    },
    navigate: {
      icon: <LocationIcon />,
      getSubLabel: (item) => item.value.address_string || '',
    },
  };

  const config = configMap[actionType] || {
    icon: null,
    getSubLabel: (item) => item.value,
  };

  return items.map((profile) => {
    const options = (profile.items || []).map((item) => ({
      ...item,
      icon: config.icon,
      subLabel: config.getSubLabel(item),
    }));

    return {
      subLabel: profile.description,
      type: profile.type,
      name: profile.name,
      label: profile.name,
      value: `${profile.type}-${profile.name}-${generateRandomCode()}`,
      options,
    };
  });
};

export const shouldShowDetailsPageQuickAction = ({
  domainType,
  inputsMap,
  workflowType,
}: {
  domainType?: DomainType;
  inputsMap?: Record<string, any>;
  workflowType?: WorkflowType;
}) => {
  if (REVYRE.COLLECT_IQ.RESTRICT_HEADER_BUTTONS.includes(workflowType || '')) {
    return false;
  }

  const { hiddenByInputKey } =
    REVYRE.COLLECT_IQ.DETAILS_PAGE_QUICK_ACTIONS_HIDDEN_RULES;

  if (domainType === 'workflow') {
    return !inputsMap?.[hiddenByInputKey];
  }

  return true;
};
